import React, { useCallback, useMemo, useState, useRef } from 'react';
import cn from 'classnames';
import Title from 'components/UI/Title';
import Slider from 'components/UI/Slider';
import ProjectGalleryCard from 'components/cards/ProjectGalleryCard';
import { pushCustomEvent } from 'utils/analytics';
import { useWindowResize } from 'utils/hooks';

import * as styles from './GallerySlider.module.scss';

export default function GallerySlider({
  id,
  title,
  projects,
  layout,
  gallery,
  setActive,
  className,
}) {
  const navItems = useMemo(() => projects?.map(item => item.project_url), [projects]);
  const { width } = useWindowResize();
  const isMobile = width < 544;
  const sliderRef = useRef(null);
  const [current, setCurrent] = useState(0);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const settings = {
    infinite: false,
    variableWidth: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 544,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          variableWidth: true,
          arrows: false,
        },
      },
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          variableWidth: true,
          arrows: false,
        },
      },
    ],
  };

  const canScrollLeft = current > 0;
  const canScrollRight = current < projects.length - 2;

  const toggleNext = () => {
    sliderRef.current.slickNext();
  };

  const togglePrev = () => {
    sliderRef.current.slickPrev();
  };

  const handleTitleClick = () => {
    setActive(id);
    pushCustomEvent('ndrsn_projects_galleries_slider', {
      action: 'click_on_title',
      gallery: id,
    });
  };

  const onSlideChange = useCallback(() => {
    pushCustomEvent('ndrsn_projects_galleries_slider', {
      action: 'slide_change',
      gallery: id,
    });
  }, [id]);

  return (
    <div className={className}>
      <div className={styles.topRow}>
        <Title tag="h2" size="h3" className={styles.title} onClick={handleTitleClick}>
          {title}
        </Title>
        <div className={styles.sliderControls}>
          <button
            onClick={togglePrev}
            className={styles.buttonLeft}
            disabled={!canScrollLeft}
            aria-label="Previous Slide"
          />
          <button
            onClick={toggleNext}
            className={styles.buttonRight}
            disabled={!canScrollRight}
            aria-label="Next Slide"
          />
        </div>
      </div>
      <Slider
        {...settings}
        beforeChange={setCurrentKey}
        afterChange={onSlideChange}
        className={cn(styles.sliderContainer, 'defaultSlider')}
        ref={sliderRef}
      >
        {projects.map((item, i) => {
          const mirroredPattern = (i + 1) % 2 === 0;
          const defaultPattern = (i + 1) % 2 === 1;
          const isBigCard = layout === 'mirrored' ? mirroredPattern : defaultPattern;
          const cardWidth = !isMobile && isBigCard ? 'mdWidth' : 'smWidth';

          return (
            <ProjectGalleryCard
              key={item.id}
              {...item}
              navItems={navItems}
              theme={cardWidth}
              gallery={gallery}
              gallerySlider
            />
          );
        })}
      </Slider>
    </div>
  );
}
